import { ShieldExclamationIcon } from "@heroicons/react/24/outline"
import { Alert } from "components/core/alert"
import { Button } from "components/core/button"
import { InputLabel } from "components/core/inputLabel"
import { SEO } from "components/core/seo"
import { Spinner } from "components/core/spinner"
import { Grid } from "components/dashboard/grid/grid"
import { getOgImage } from "components/dashboard/ogImage"
import { BASE_URL } from "lib/utils"
import { FC } from "react"
import { usePasswordProtectedDashboard, useVerifyDashboardPassword } from "reactQuery/usePasswordProtected"

interface Props {
  dashboardId: number
}

export const PasswordProtection: FC<Props> = ({ dashboardId }) => {
  const { mutateAsync: verifyDashboardPassword, isError, isLoading: isVerifyingPassword } = useVerifyDashboardPassword()
  const { data: data, isLoading } = usePasswordProtectedDashboard(dashboardId)

  if (isLoading) {
    return (
      <div className="w-full pt-44 md:pt-64 flex items-center justify-center">
        <Spinner className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
      </div>
    )
  }

  if (data?.dashboard) {
    return (
      <>
        <SEO
          title={data.dashboard.name}
          description={`A dashboard by ${data.dashboard.owner_name}`}
          ogImage={getOgImage(data.dashboard)}
        />
        <Grid dashboard={data.dashboard} />
      </>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="px-4 py-16 sm:px-6 sm:py-24 md:grid md:place-items-center lg:px-8">
        <SEO
          title="Protected Dashboard"
          description="Frontend Toolkit offers customizable dashboards for your recurring Frontend tasks. Base64 encoder/decoder, SVG optimizations, SVG to JSX and many more!"
          ogImage={{
            title: "Password protected dashboard",
            url: `${BASE_URL}/dashboard/${data?.dashboard.id}`,
          }}
        />
        <div className="max-w-max mx-auto">
          <form
            className="sm:flex"
            onSubmit={async (ev) => {
              ev.preventDefault()
              try {
                await verifyDashboardPassword({ dashboardId, password: (ev.target as any).password.value })
              } catch (err) {}
            }}
          >
            <p className="text-4xl font-extrabold text-indigo-600 sm:text-5xl">
              <ShieldExclamationIcon className="w-16 h-16" />
            </p>
            <div className="sm:ml-6">
              <div className="sm:border-l sm:border-gray-200 sm:dark:border-gray-800/50 sm:pl-6">
                <h1 className="text-4xl font-extrabold text-gray-900 dark:text-gray-200 tracking-tight sm:text-5xl">
                  Protected Dashboard
                </h1>
                <div className="space-y-4">
                  <p className="mt-1 text-base text-gray-500">Please enter the password to access this page</p>
                  {isError && (
                    <Alert type="error" className="mt-6">
                      That did not work
                    </Alert>
                  )}
                  <InputLabel type="password" id="password" label="Password" />
                  <Button type="submit" variant="primary" disabled={isVerifyingPassword} loading={isVerifyingPassword}>
                    Send
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
